import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const TICKETS_API = '/api/tickets';
const MASTER_DATA_API = '/api/master-data';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    refresh: ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10', 'M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2', 'M10 11v6', 'M14 11v6'],
    reply: ['M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-11.7 1 1 0 0 1 1 1 8.5 8.5 0 0 1-1.3 4.4l3.8 3.8a1 1 0 0 1 0 1.4z', 'M3 21l1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
};

export default function Tickets({ onReply, onCreate }) {
    const [tickets, setTickets] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [filterDepartment, setFilterDepartment] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterService, setFilterService] = useState('');

    const [showImportScreen, setShowImportScreen] = useState(false);

    const handleExport = () => {
        if (tickets.length === 0) { alert('No tickets to export.'); return; }
        const exportData = tickets.map(t => ({
            'Subject': t.subject || '',
            'Description': t.description || '',
            'Contact Name': t.contactName || '',
            'Contact Email': t.contactEmail || '',
            'Department': t.department?.name || '',
            'Service': t.service?.name || '',
            'Priority': t.priority?.name || '',
            'Status': t.status?.name || '',
            'Created Date': t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 25 }, { wch: 30 }, { wch: 15 },
            { wch: 25 }, { wch: 15 }, { wch: 15 },
            { wch: 12 }, { wch: 12 }, { wch: 15 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Tickets_Export_${timestamp}.xlsx`);
    };

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const [tRes, sRes, dRes, svRes] = await Promise.all([
                axios.get(`${TICKETS_API}?superadmin=true`),
                axios.get(MASTER_DATA_API),
                axios.get('/api/departments/all'),
                axios.get('/api/services')
            ]);
            setTickets(tRes.data);
            setStatuses(sRes.data.filter(i => i.category === 'ticket-status'));
            setPriorities(sRes.data.filter(i => i.category === 'ticket-priority'));
            setDepartments(dRes.data);
            setServices(svRes.data);
        } catch (err) {
            console.error('Error fetching tickets:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to delete this ticket?")) return;
        try {
            await axios.delete(`${TICKETS_API}/${id}`);
            fetchTickets();
        } catch (err) {
            console.error("Delete error:", err);
            alert("Failed to delete ticket");
        }
    };

    const filteredTickets = tickets.filter(t => {
        const matchesSearch = 
            (t.subject || '').toLowerCase().includes(search.toLowerCase()) ||
            (t.description || '').toLowerCase().includes(search.toLowerCase()) ||
            (t.contactName || '').toLowerCase().includes(search.toLowerCase()) ||
            (t.contactEmail || '').toLowerCase().includes(search.toLowerCase());

        const matchesDepartment = !filterDepartment || (t.department?._id === filterDepartment || t.department?.name === filterDepartment);
        const matchesPriority = !filterPriority || (t.priority?._id === filterPriority || t.priority?.name === filterPriority);
        const matchesStatus = !filterStatus || (t.status?._id === filterStatus || t.status?.name === filterStatus);
        const matchesService = !filterService || (t.service?._id === filterService || t.service?.name === filterService);

        return matchesSearch && matchesDepartment && matchesPriority && matchesStatus && matchesService;
    });

    if (showImportScreen) {
        return (
            <TicketImportWizard
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchTickets(); }}
            />
        );
    }

    const tdStyle = { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14.49px', color: '#1e293b', verticalAlign: 'middle' };
    const thStyle = { background: '#f8fafc', color: '#64748b', fontWeight: 'bold', fontSize: '13.36px', padding: '12px 16px', borderBottom: '1px solid #e2e8f0', textAlign: 'left', textTransform: 'uppercase' };

    return (
        <div style={{ padding: '24px 32px', background: '#f8fafc', minHeight: 'calc(100vh - 70px)' }}>
            <div className="superadmin-page-header">
                <div className="header-titles">
                    <h1>Ticket Index</h1>
                </div>
                <div className="header-actions">
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button style={{
                        padding: '8px 16px', borderRadius: '6px', border: '1px solid #c3dcf5', background: '#eef2ff',
                        display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15.60px', cursor: 'pointer', color: '#11427D', fontWeight: 500
                    }} onClick={() => setShowImportScreen(true)}>
                        <Icon d={icons.upload} size={15} /> Import
                    </button>
                    <button style={{
                        padding: '8px 16px', borderRadius: '6px', border: '1px solid #a7f3d0', background: '#ecfdf5',
                        display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15.60px', cursor: 'pointer', color: '#065f46', fontWeight: 500
                    }} onClick={handleExport}>
                        <Icon d={icons.download} size={15} /> Export
                    </button>
                    <button style={{
                        padding: '8px 16px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff',
                        display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15.60px', cursor: 'pointer', color: '#64748b'
                    }} onClick={fetchTickets}>
                        <Icon d={icons.refresh} size={15} /> Refresh
                    </button>
                    <button style={{
                        padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#1e293b',
                        display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15.60px', cursor: 'pointer', color: '#fff'
                    }} onClick={onCreate}>
                        <Icon d={icons.plus} size={15} /> New Ticket
                    </button>
                </div>
            </div>
            </div>

            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
                    <div style={{ position: 'relative', width: '280px' }}>
                        <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                            <Icon d={icons.search} size={15} />
                        </div>
                        <input
                            type="text"
                            placeholder="Search tickets..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                                width: '100%', padding: '10px 12px 10px 38px', borderRadius: '8px', border: '1px solid #e2e8f0',
                                outline: 'none', fontSize: '14.5px', boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <select
                        value={filterDepartment}
                        onChange={(e) => setFilterDepartment(e.target.value)}
                        style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', color: '#334155', fontSize: '14px', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
                    >
                        <option value="">All Departments</option>
                        {departments.map(d => (
                            <option key={d._id} value={d._id}>{d.name}</option>
                        ))}
                    </select>

                    <select
                        value={filterPriority}
                        onChange={(e) => setFilterPriority(e.target.value)}
                        style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', color: '#334155', fontSize: '14px', outline: 'none', cursor: 'pointer', minWidth: '150px' }}
                    >
                        <option value="">All Priorities</option>
                        {priorities.map(p => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                    </select>

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', color: '#334155', fontSize: '14px', outline: 'none', cursor: 'pointer', minWidth: '150px' }}
                    >
                        <option value="">All Statuses</option>
                        {statuses.map(s => (
                            <option key={s._id} value={s._id}>{s.name}</option>
                        ))}
                    </select>

                    <select
                        value={filterService}
                        onChange={(e) => setFilterService(e.target.value)}
                        style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', color: '#334155', fontSize: '14px', outline: 'none', cursor: 'pointer', minWidth: '150px' }}
                    >
                        <option value="">All Services</option>
                        {services.map(sv => (
                            <option key={sv._id} value={sv._id}>{sv.name}</option>
                        ))}
                    </select>

                    {(filterDepartment || filterPriority || filterStatus || filterService || search) && (
                        <button
                            onClick={() => {
                                setFilterDepartment('');
                                setFilterPriority('');
                                setFilterStatus('');
                                setFilterService('');
                                setSearch('');
                            }}
                            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            Reset Filters
                        </button>
                    )}
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...thStyle, width: '40px' }}><input type="checkbox" /></th>
                                <th style={{ ...thStyle, width: '60px' }}>S.No</th>
                                <th style={thStyle}>Subject</th>
                                <th style={thStyle}>Status</th>
                                <th style={thStyle}>Priority</th>
                                <th style={thStyle}>Department</th>
                                <th style={thStyle}>Service</th>
                                <th style={thStyle}>Contact</th>
                                <th style={thStyle}>Created</th>
                                <th style={{ ...thStyle, textAlign: 'center', width: '120px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="10" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading tickets...</td></tr>
                            ) : filteredTickets.length === 0 ? (
                                <tr><td colSpan="10" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>No tickets matching your filters</td></tr>
                            ) : filteredTickets.map((ticket, idx) => (
                                <tr key={ticket._id} onClick={() => onReply(ticket)} style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                >
                                    <td style={tdStyle}><input type="checkbox" onClick={e => e.stopPropagation()} /></td>
                                    <td style={{ ...tdStyle, color: '#64748b' }}>{idx + 1}</td>
                                    <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>{ticket.subject}</td>
                                    <td style={tdStyle}>
                                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                            {ticket.tags && ticket.tags.length > 0 ? ticket.tags.map((tag, i) => {
                                                const matchedStatus = statuses.find(s => s.name.toLowerCase() === tag.toLowerCase());
                                                const color = matchedStatus?.color || '#64748b';
                                                return (
                                                    <span key={i} style={{
                                                        background: `${color}15`, color: color,
                                                        padding: '3px 10px', borderRadius: '12px', fontSize: '12.25px', fontWeight: 600
                                                    }}>{tag}</span>
                                                );
                                            }) : (
                                                <span style={{ color: '#94a3b8', fontSize: '13.36px' }}>—</span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={tdStyle}>
                                        {ticket.priority ? (
                                            <span style={{
                                                background: `${ticket.priority.color || '#64748b'}15`,
                                                color: ticket.priority.color || '#64748b',
                                                padding: '3px 10px', borderRadius: '12px', fontSize: '12.25px', fontWeight: 600
                                            }}>{ticket.priority.name}</span>
                                        ) : '—'}
                                    </td>
                                    <td style={tdStyle}>{ticket.department?.name || '—'}</td>
                                    <td style={tdStyle}>{ticket.service?.name || '—'}</td>
                                    <td style={tdStyle}>
                                        {ticket.contactName || ticket.contactEmail
                                            ? <span>{ticket.contactName}{ticket.contactName && ticket.contactEmail ? ' - ' : ''}{ticket.contactEmail}</span>
                                            : '—'}
                                        {ticket.operatorId && ticket.operatorId.companyName && (
                                            <div style={{ fontSize: '11.5px', color: '#16539C', marginTop: '4px', fontWeight: 600 }}>
                                                🏢 Operator: {ticket.operatorId.companyName}
                                            </div>
                                        )}
                                    </td>
                                    <td style={tdStyle}>{new Date(ticket.createdAt).toLocaleDateString()}</td>
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onReply(ticket); }}
                                                title="Reply"
                                                style={{
                                                    background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8',
                                                    padding: '4px', borderRadius: '4px', display: 'inline-flex', transition: 'color 0.15s'
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.color = '#10b981'}
                                                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                                            >
                                                <Icon d={icons.reply} size={15} />
                                            </button>
                                            <button
                                                onClick={(e) => handleDelete(e, ticket._id)}
                                                title="Delete"
                                                style={{
                                                    background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8',
                                                    padding: '4px', borderRadius: '4px', display: 'inline-flex', transition: 'color 0.15s'
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                                                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                                            >
                                                <Icon d={icons.trash} size={15} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function TicketImportWizard({ onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                // Standardize headers
                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Subject': 'Cannot log in to operator panel',
                'Description': 'Getting 500 error when clicking submit on the operators management page.',
                'Contact Name': 'Alice Cooper',
                'Contact Email': 'alice@example.com',
                'Department': 'IT',
                'Service': 'General Support',
                'Priority': 'High',
                'Status': 'Answered'
            },
            {
                'Subject': 'Invoicing question',
                'Description': 'Need to update payment terms on invoice template.',
                'Contact Name': 'Bob Dylan',
                'Contact Email': 'bob@example.com',
                'Department': 'Finance',
                'Service': 'Billing',
                'Priority': 'Low',
                'Status': 'Open'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        ws['!cols'] = [
            { wch: 25 }, { wch: 30 }, { wch: 15 },
            { wch: 25 }, { wch: 15 }, { wch: 15 },
            { wch: 12 }, { wch: 12 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'Tickets_Import_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const res = await axios.post(`/api/tickets/import`, { tickets: parsedData });
            setResult(res.data);
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    if (result) {
        return (
            <div style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The tickets data has been imported successfully. New departments, services, priorities, and statuses were created automatically if they did not exist.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '500px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>Tickets Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>

                    {result.errors && result.errors.length > 0 && (
                        <div style={{ maxWidth: '750px', margin: '0 auto 30px', textAlign: 'left' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>Details / Warnings:</h4>
                            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
                                {result.errors.map((err, idx) => (
                                    <div key={idx} style={{ fontSize: '13px', color: '#64748b', padding: '6px 0', borderBottom: idx < result.errors.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                        ⚠️ Row {err.row}: {err.message}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onDone}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}
                    >
                        Return to Tickets
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                        onClick={onBack}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import Tickets</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new tickets via Excel upload to organize customer requests efficiently.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    {/* Instructions Left Column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ℹ️ How it works
                            </h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li>It will automatically associate tickets with matched Departments, Services, Priorities, and Statuses in the system.</li>
                                <li style={{ fontWeight: '600', color: '#16539C' }}>New Departments, Services, Priorities, and Statuses will be created automatically if they do not exist!</li>
                            </ul>
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {['Subject *', 'Description', 'Contact Name', 'Contact Email', 'Department', 'Service', 'Priority', 'Status'].map((f, i) => (
                                    <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: '6px' }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                            <p style={{ fontSize: '11.5px', color: '#64748b', marginTop: '10px', margin: '10px 0 0' }}>* <strong>Subject</strong> is required to create a ticket.</p>
                        </div>

                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button
                                onClick={downloadTemplate}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>

                    {/* Upload Box Right Column */}
                    <div
                        onDragEnter={handleDrag}
                        onDragOver={handleDrag}
                        onDragLeave={handleDrag}
                        onDrop={handleDrop}
                        style={{
                            background: 'white',
                            borderRadius: '16px',
                            border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            backgroundColor: dragActive ? '#f0f7ff' : 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px 40px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                            transition: 'all 0.15s',
                            cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('ticket-file-upload-input').click()}
                    >
                        <input
                            id="ticket-file-upload-input"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                            Supports .xlsx, .xls formats
                        </span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9', marginBottom: '20px' }}>
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Review Tickets</h3>
                            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '4px 0 0' }}>
                                Found <strong>{parsedData.length} records</strong>. Click proceed to import them to the ticketing database.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => { setFile(null); setParsedData([]); }}
                                style={{ padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Reset
                            </button>
                            <button
                                onClick={handleImport}
                                disabled={importing}
                                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)', opacity: importing ? 0.7 : 1 }}
                            >
                                {importing ? 'Processing...' : `Proceed Import (${parsedData.length})`}
                            </button>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto', maxHeight: '350px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Subject</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Description</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Contact Name</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Contact Email</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Department</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Service</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Priority</th>
                                    <th style={{ padding: '12px 16px', fontWeight: '700', color: '#475569' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((row, idx) => {
                                    const subject = row.subject || row['Subject'] || '';
                                    const desc = row.description || row['Description'] || '';
                                    const contactName = row.contactName || row['Contact Name'] || '';
                                    const contactEmail = row.contactEmail || row['Contact Email'] || '';
                                    const dept = row.department || row['Department'] || '';
                                    const serv = row.service || row['Service'] || '';
                                    const prio = row.priority || row['Priority'] || '';
                                    const stat = row.status || row['Status'] || '';

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px 16px', fontWeight: '600' }}>{subject || <span style={{ color: '#ef4444' }}>Missing Subject</span>}</td>
                                            <td style={{ padding: '12px 16px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{desc}</td>
                                            <td style={{ padding: '12px 16px' }}>{contactName}</td>
                                            <td style={{ padding: '12px 16px' }}>{contactEmail}</td>
                                            <td style={{ padding: '12px 16px' }}>{dept}</td>
                                            <td style={{ padding: '12px 16px' }}>{serv}</td>
                                            <td style={{ padding: '12px 16px' }}>{prio}</td>
                                            <td style={{ padding: '12px 16px' }}>{stat}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
